import { ServiceIndisponibleError, SessionExpireeError, getApi } from '@/lib/api';
import { lireSession } from '@/lib/session';
import { INTERVALLE_LECTURE_MS, cleTraduction } from '@/lib/use-lecture-traduction';
import type { ResumeTraduction, Traduction } from '@rosetta/shared';
import { useQueries, useQuery } from '@tanstack/react-query';

/**
 * The query key of the history: `['historique']` and the user it belongs to, so that the
 * list of one account is never shown to the next one signed in on the same browser.
 */
export const CLE_HISTORIQUE = ['historique'] as const;
export const cleHistorique = (utilisateurId: string | undefined) =>
  [...CLE_HISTORIQUE, utilisateurId ?? 'sans-session'] as const;

/** A blip of the service is retried twice; every other error is final. */
const NOMBRE_ESSAIS = 2;
const reessayerSiServiceIndisponible = (essais: number, erreur: Error) =>
  erreur instanceof ServiceIndisponibleError && essais < NOMBRE_ESSAIS;

/** What the history screen reads: the list, each card in its current state. */
export interface LectureHistorique {
  data: ResumeTraduction[] | undefined;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

/**
 * Reads the history through the `RosettaApi` (never a `fetch` from a component), with the
 * stored session looked up at every read (it can expire while the page is open). The list is
 * read when the page opens (always stale: a user who comes back from a translation sees it, not
 * the list of a minute ago), and only then (B7, #13): it used to be read whole every 5 s while
 * one card was running, two reads of up to 1 000 rows each time.
 *
 * A card that is running is followed on its own instead: its translation is read every 5 s
 * (`lireTraduction`, the query the waiting screen W1 and the follow-up of #5 share) until it
 * leaves "en cours", and the card takes the state that read gives: finished, partial, failed,
 * interrupted or blocked (#13: a step failed; the reading stops there, the card offers
 * "interrompre" and "relancer"). A session that expired during one of these reads is the error of
 * the whole screen.
 */
export function useHistorique(): LectureHistorique {
  const liste = useQuery<ResumeTraduction[], Error>({
    queryKey: cleHistorique(lireSession()?.utilisateur.id),
    queryFn: () => {
      const session = lireSession();
      if (session === null) throw new SessionExpireeError();
      return getApi().listerTraductions(session);
    },
    staleTime: 0,
    retry: reessayerSiServiceIndisponible,
  });

  const enCours = (liste.data ?? []).filter((resume) => resume.statut === 'en_cours');
  const lectures = useQueries({
    queries: enCours.map((resume) => ({
      queryKey: cleTraduction(resume.id),
      queryFn: (): Promise<Traduction> => {
        const session = lireSession();
        if (session === null) throw new SessionExpireeError();
        return getApi().lireTraduction(session, resume.id);
      },
      // The 5 s that follow are the retry: a blip of the service does not stop the card.
      retry: false,
      refetchInterval: (requete: {
        state: { data: Traduction | undefined; error: Error | null };
      }) =>
        requete.state.error instanceof SessionExpireeError ||
        (requete.state.data !== undefined && requete.state.data.statut !== 'en_cours')
          ? false
          : INTERVALLE_LECTURE_MS,
    })),
  });

  const lues = new Map<string, Traduction>();
  let sessionExpiree: Error | null = null;
  for (const lecture of lectures) {
    if (lecture.data !== undefined) lues.set(lecture.data.id, lecture.data);
    if (lecture.error instanceof SessionExpireeError) sessionExpiree = lecture.error;
  }

  const data = liste.data?.map((resume) => {
    const lue = lues.get(resume.id);
    return lue === undefined || lue.statut === resume.statut
      ? resume
      : { ...resume, statut: lue.statut };
  });

  return { data, error: liste.error ?? sessionExpiree, refetch: liste.refetch };
}
